import * as React from 'react';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import { SuggestionSelect, ISuggestionValue } from '../../../../_common/platform/tsx/SuggestionSelect';
import SuggestionListField from '../../../../_common/platform/tsx/SuggestionListField';
import NumberField from '../../../../_common/platform/tsx/NumberField';
import StringField from '../../../../_common/platform/tsx/StringField';
import { TIME_PART_TYPES } from '../../../../_common/platform/ts/ConfigurationConstants';
import { TimePartControlType } from '../../../../_common/platform/tsx/filtering/Interfaces';
import { IValuesCollection } from '../../../platform/ts/appTheme';
import * as FilterUtilities from '../../../../_common/platform/tsx/filtering/FilterUtilities';
import * as Globalization from '../../../../_common/platform/ts/Globalization';
import { IWidth } from '../../ts/Interfaces';

async function createDisplayValues(
    timePartType: string,
    values: ISuggestionValue[],
    minYear: number,
    maxYear: number,
    theme: IValuesCollection
): Promise<ISuggestionValue[]> {
    return new Promise<ISuggestionValue[]>((resolve) => {
        const result: ISuggestionValue[] = [];
        const range = FilterUtilities.getTimePartValueRange(timePartType, minYear || 2000, maxYear || 2050);

        if (range == null) {
            resolve(result);
        }

        Globalization.applyWidgetCulture(theme, (culture) => {
            let start = range.start;
            let end = range.end;

            if (timePartType === TIME_PART_TYPES.MONTH) {
                for (let i = start; i <= end; i++) {
                    const value = Globalization.getMonthName_Wide(i);

                    result.push({
                        value: i.toString(),
                        displayValue: value
                    });
                }
            } else if (timePartType === TIME_PART_TYPES.DAY_OF_WEEK) {
                const firstWeekDay = Globalization.getFirstWeekDay(theme);

                let dayToDisplayNumber = Globalization.WEEK_DAY_ABBREVIATIONS_ISO.indexOf(firstWeekDay) + 1;

                for (let counter = start; counter <= end; counter++) {
                    const displayValue = Globalization.getWeekDayNameByAbbreviation_Wide(
                        Globalization.WEEK_DAY_ABBREVIATIONS_ISO[dayToDisplayNumber - 1]
                    );

                    result.push({
                        value: dayToDisplayNumber.toString(),
                        displayValue: displayValue
                    });

                    if (dayToDisplayNumber < end) {
                        dayToDisplayNumber++;
                    } else {
                        dayToDisplayNumber = start;
                    }
                }
            } else {
                if (timePartType === TIME_PART_TYPES.YEAR || timePartType === TIME_PART_TYPES.FISCAL_YEAR) {
                    if (values != null) {
                        values.forEach((value) => {
                            const valueInt = parseInt(value.value, 10);

                            if (!isNaN(valueInt)) {
                                if (valueInt < start) {
                                    start = valueInt;
                                }

                                if (valueInt > end) {
                                    end = valueInt;
                                }
                            }
                        });
                    }
                }

                for (let i = start; i <= end; i++) {
                    const value = i.toString();

                    result.push({
                        value: value,
                        displayValue: value
                    });
                }
            }

            resolve(result);
        });
    });
}

export default function TimePartSelect(props: {
    type: string;
    singleSelectMode: boolean;
    allowNull: boolean;
    theme: IValuesCollection;
    value?: ISuggestionValue[];
    timePartControlType?: TimePartControlType;
    minYear?: number;
    maxYear?: number;
    placeholder?: string;
    usePlaceholderAsTitle?: boolean;
    tooltip?: string;
    name?: string;
    width?: IWidth;
    glob: IGlob;

    onChange?(value: ISuggestionValue[]): void;
}) {
    const [displayValues, setDisplayValues] = React.useState<ISuggestionValue[]>();

    React.useEffect(() => {
        if (props.type !== TIME_PART_TYPES.MILLISECOND) {
            createDisplayValues(props.type, props.value, props.minYear, props.maxYear, props.theme).then((values) => {
                setDisplayValues(values);
            });
        }
    }, [props.type, props.value, props.minYear, props.maxYear, props.theme]);

    if (props.type === TIME_PART_TYPES.MILLISECOND) {
        const handleOnNumberFieldChange = (value: number) => {
            if (props.onChange == null) {
                return;
            }

            const res: ISuggestionValue[] =
                value != null
                    ? [
                          {
                              value: value.toString()
                          }
                      ]
                    : null;

            props.onChange(res);
        };

        const handleOnStringFieldChange = (value: string) => {
            if (props.onChange == null) {
                return;
            }

            let result: ISuggestionValue[] = null;

            if (value != null) {
                const values = value.split(',');

                if (values.length > 0) {
                    const range = FilterUtilities.getTimePartValueRange(TIME_PART_TYPES.MILLISECOND);

                    result = values
                        .filter((value) => {
                            return FilterUtilities.isInRange(value, range.start, range.end);
                        })
                        .map<ISuggestionValue>((value) => {
                            return {
                                value: value
                            };
                        });
                }
            }

            props.onChange(result);
        };

        if (props.singleSelectMode) {
            let value: number = null;

            if (props.value != null && props.value.length > 0) {
                value = parseInt(props.value[0].value, 10);
            }

            return (
                <NumberField
                    value={value}
                    min={0}
                    max={59999}
                    allowNull={props.allowNull}
                    integer={true}
                    placeholder={props.placeholder}
                    onChange={handleOnNumberFieldChange}
                />
            );
        } else {
            let value: string = null;

            if (props.value != null && props.value.length > 0) {
                value = props.value.reduce((res, curValue) => {
                    return res + (res.length === 0 ? curValue.value : ',' + curValue.value);
                }, '');
            }

            return (
                <StringField
                    value={value}
                    allowNull={props.allowNull}
                    placeholder={props.placeholder}
                    changeOnBlurOnly={true}
                    onChange={handleOnStringFieldChange}
                />
            );
        }
    } else {
        if (displayValues == null) {
            return null;
        }

        if (props.timePartControlType === 'list') {
            return (
                <SuggestionListField
                    value={props.value}
                    displayValues={displayValues}
                    keySelect={true}
                    singleSelectMode={props.singleSelectMode}
                    allowNull={props.allowNull}
                    hideSearchField={true}
                    theme={props.theme}
                    width={props.width}
                    onChange={props.onChange}
                />
            );
        } else {
            return (
                <SuggestionSelect
                    value={props.value}
                    displayValues={displayValues}
                    keySelect={true}
                    singleSelectMode={props.singleSelectMode}
                    allowNull={props.allowNull}
                    hideSearchField={true}
                    placeholder={props.placeholder}
                    usePlaceholderAsTitle={props.usePlaceholderAsTitle}
                    tooltip={props.tooltip}
                    theme={props.theme}
                    name={props.name}
                    width={props.width}
                    glob={props.glob}
                    onChange={props.onChange}
                />
            );
        }
    }
}
